import axios from "axios";
import myApp from "../main";
import { getToken } from "./auth-token";

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
let requestUrl = [];

const maxRetries = 5;
const retryDelay = 1000;

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export const http = (baseURL) => {
  const axiosInstance = axios.create({
    baseURL,
    json: true,
  });
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Request interceptor
  axiosInstance.interceptors.request.use(
    async (config) => {
      if (!config.headers["isSilentAPI"]) {
        increaseRequestCount(config.url);
      }
      let token = await myApp.$auth.getTokenSilently();
      let tenantId = sessionStorage.getItem("tenantId");
      let retries = 0;

      while (!tenantId && retries < maxRetries) {
        retries++;
        await delay(retryDelay);
        tenantId = sessionStorage.getItem("tenantId");
      }

      if (!tenantId) {
        myApp.$store.commit("setAPIErrorKey", {
          key: "lblTenantNullMessage",
          errorType: "info",
        });
        return Promise.reject(new Error("Tenant ID not found"));
      }
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
        config.headers["x-tenant-id"] = sessionStorage.getItem("tenantId");
        config.headers["time-zone"] = timeZone;
        config.headers["tab_id"] = sessionStorage.getItem("tab_id");
      }
      return config;
    },
    (error) => {
      // Do something with request error TODO
      clearRequestList();
      return Promise.reject(error);
    }
  );

  // Response interceptor
  axiosInstance.interceptors.response.use(
    function (response) {
      decreaseRequestCount(response.config.url);
      const firstDigitStr = String(response.status)[0];
      if (Number(firstDigitStr) === 4) {
        myApp.$store.commit("setAPIErrorMessage", response.data.error_message);
      } else if (
        response.status !== 200 &&
        response.status !== 204 &&
        response.status !== 202
      ) {
        myApp.$store.commit("setAPIErrorStatus", true);
      }
      if (response.config.responseType === "blob") {
        return response;
      } else {
        return escapeNestedData(response);
      }
    },
    function (error) {
      clearRequestList();
      if (error.response && error.response.status) {
        const firstDigitStr = String(error.response.status)[0];
        if (Number(firstDigitStr) === 4) {
          if (error.response.status === 401) {
            const index = error.response.config.url.indexOf("meeting/");
            if (error.response.config.method === "get" && index > -1) {
              myApp.$router.push({ path: "/forbidden" });
            } else {
              myApp.$store.commit(
                "setAPIErrorMessage",
                error.response.data.error_message
              );
            }
          } else if (error.response.status === 406) {
            myApp.$store.commit("setScheduleMeetingError", true);
          } else {
            const { error_message } = error.response.data
            if (error_message !== "movetopic") {
              if (error.response.data.jp_response_key) {
                if(error.response.data.jp_response_key === "MissingColumns") {
                  myApp.$store.commit("setAPIErrorKey", {
                    key: error.response.data.jp_response_key,
                    missing_columns: error.response.data?.extra_data?.missing_columns,
                    errorType: "info",
                  });
                } else if (error.response.data.jp_response_key === "DecodeError") {
                  myApp.$store.commit("setAPIErrorKey", {
                    key: error.response.data.jp_response_key,
                    error_message: error_message,
                    errorType: "error", 
                  });
                } else if (error.response.data.jp_response_key === "incorrectEmail") {
                  myApp.$store.commit("setAPIErrorKey", {
                    key: error.response.data.error_message,
                    error_message: error_message,
                    errorType: "error", 
                  });
                } else if (error.response.data.jp_response_key === "invalidColumnCount") {
                  myApp.$store.commit("setAPIErrorKey", {
                    key: error.response.data.jp_response_key,
                    error_message: error_message,
                    column_count: error.response.data?.extra_data?.columns,
                    errorType: "error", 
                  });
                } else {
                  myApp.$store.commit("setAPIErrorKey", {
                    key: error.response.data.jp_response_key,
                    errorType: "info",
                  });
                }
              } else {
                myApp.$store.commit(
                  "setAPIErrorMessage",
                  error.response.data.error_message
                );
              }
            }
          }
        } else if (
          error.response.status !== 200 &&
          error.response.status !== 204 &&
          error.response.status !== 202
        ) {
          myApp.$store.commit("setAPIErrorStatus", true);
        } else {
          myApp.$store.commit("setAPIErrorStatus", true);
        }
      } else {
        if (
          error.error &&
          error.error === "login_required" &&
          myApp.$router.currentRoute.path !== "/sessionexpired"
        ) {
          myApp.$router.push({ path: "/sessionexpired" });
        } else if (error.message == "Network Error") {
          myApp.$store.commit("setAPIErrorKey", {
            key: "msgNetworkError",
            errorType: "error",
          });
        }
      }
      return Promise.reject(error);
    }
  );
  return axiosInstance;
};

function increaseRequestCount(url) {
  requestUrl.push(url);
  // console.log(requestUrl, "request")
  if (requestUrl.length) {
    myApp.$loader.show();
  }
}

function clearRequestList() {
  requestUrl = [];
  myApp.$loader.hide();
}

function decreaseRequestCount(url) {
  requestUrl = removeFirstOccurrence(requestUrl, url);
  // console.log(requestUrl, "response")
  if (requestUrl.length === 0) {
    myApp.$loader.hide();
  }
}

function removeFirstOccurrence(array, value) {
  const index = array.indexOf(value);
  if (index !== -1) {
    array.splice(index, 1);
  }
  return array;
}

function escapeNestedData(data) {
  if (typeof data === "string") {
    return myApp.$unescapeHtml(data);
  }

  if (Array.isArray(data)) {
    return data.map((item) => escapeNestedData(item));
  }

  if (typeof data === "object" && data !== null) {
    const escapedData = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        escapedData[myApp.$unescapeHtml(key)] = escapeNestedData(data[key]);
      }
    }
    return escapedData;
  }

  if (data instanceof Uint8Array) {
    return Buffer.from(data).toString("hex");
  }

  return data;
}

export default {
  http,
};
